import { z } from 'zod';

import { chatEventIdSchema, chatMessageIdSchema, chatThreadIdSchema } from './chat-ids';

/** The question a legacy turn blocks on; the legacy chat answers it with a callback for `eventId`/`blockId`. */
export const awaitingCallbackSchema = z.object({
    blockId: z.uuid(),
    eventId: chatEventIdSchema,
    expiresAt: z.string().nullish(),
});

export type AwaitingCallback = z.infer<typeof awaitingCallbackSchema>;

/** The interrupt a pending turn waits on; a run answers it with a resume entry for `blockId`. */
export const openInterruptSchema = z.object({
    blockId: z.uuid(),
    expiresAt: z.string(),
});

export type OpenInterrupt = z.infer<typeof openInterruptSchema>;

export const pendingTurnSchema = z.object({
    awaitingCallback: awaitingCallbackSchema.nullable().optional(),
    openInterrupt: openInterruptSchema.nullish(),
    messageId: chatMessageIdSchema,
    since: z.string(),
});

export type PendingTurn = z.infer<typeof pendingTurnSchema>;

export const activeRunSchema = z.object({
    expiresAt: z.string(),
});

export type ActiveRun = z.infer<typeof activeRunSchema>;

export const threadRunStatusSchema = z.enum(['idle', 'resumable', 'running']);

export type ThreadRunStatus = z.infer<typeof threadRunStatusSchema>;

export const threadSchema = z.object({
    activeRun: activeRunSchema.nullish(),
    createdAt: z.string(),
    id: chatThreadIdSchema,
    name: z.string().nullable().optional(),
    pendingTurn: pendingTurnSchema.nullable().optional(),
    status: threadRunStatusSchema.nullish(),
    unreadMessagesAvailable: z.boolean().nullish(),
    updatedAt: z.string(),
});

export type Thread = z.infer<typeof threadSchema>;

export const threadListSchema = z.object({
    threads: z.array(threadSchema),
});

export type ThreadList = z.infer<typeof threadListSchema>;

export const createThreadRequestSchema = z.object({}).strict();

export type CreateThreadRequest = z.infer<typeof createThreadRequestSchema>;
